// MACHINERY V2 (Stroje) — Prehľad: the LIVE STATUS card (M2.16d, board 20 top).
//
// Board 20 draws: status chip + connectivity dot on one row, a mono program line
// under it, then a row of KPI cells. The first two are not rebuilt here —
// ProfileLiveRibbon already paints exactly that composition off the M2.1
// presentation object, so this card MOUNTS it rather than re-inlining a badge.
// That is what keeps the hero's ribbon and this card from ever disagreeing about
// one machine, and it is why nothing in this file tests `alarms.length`, `conn`
// or `status`.
//
// ============================================================
//  WHAT THE BOARD DRAWS THAT THE WIRE CANNOT CARRY
// ============================================================
// Each is commented at its site so nobody "finishes" it from the wrong source.
//
//  1. "plc · 2ms" (top-right of the board's card) is an agent round-trip stamp.
//     `GET /v1/gateway/machines` carries no per-machine latency and no heartbeat
//     age at all (fleet/fleetTelemetry.ts:14-21 enumerates what the wire does and
//     does not carry), so the stamp is NOT rendered. A fabricated "2ms" is a lie
//     a shop floor acts on.
//
//  2. THE THIRD KPI CELL ("S1 62 / % load") IS NOT BUILT, AND THAT IS NOT THE
//     SAME AS THE OTHER TWO BEING EMPTY. `MachineryV2Telemetry` (fleet/
//     cardStates.ts:113-127) declares `spindleRpm` and `feedPercent` — real
//     members the deferred §6/§7 backend fills — but has NO spindle-load member,
//     and the summary endpoint carries none either. A cell wired to nothing is
//     empty BY CONSTRUCTION rather than empty UNTIL the backend lands, and a
//     value that can never arrive is a defect's signature, not a design's.
//     Adding a member to the LOCKED M2.1 contract is not this sub-phase's call;
//     it is filed as a backend ask beside the three M2.16c filed. Two cells ship.
//
//  3. THE TWO CELLS THAT DO SHIP ARE SPARSE-HONEST. Today's adapter returns
//     `{...unconnectedTelemetry(), conn, status}` (fleet/fleetTelemetry.ts:124),
//     so both values are null for every machine right now and both cells render
//     an em-dash. They are present-but-empty ON PURPOSE: the card is not hidden
//     because its numbers have not arrived, and the moment the adapter starts
//     carrying them the cells fill with no component change.
//
// THE CELLS FOLLOW THE STATUS ROW, NOT THE CARD. They render on
// `presentation.showStatusRow` — the contract's own "this machine has a gateway
// connection" flag (cardStates.ts:276). §4.3 is explicit that an UNCONNECTED
// machine renders nothing of the live surface: no badge, no dot. KPI cells are
// part of that surface, so they are suppressed with it and the card falls back
// to the ribbon's no-telemetry note alone. Re-tested nowhere — the flag is read,
// never re-derived.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import {
  MachineryV2CardPresentation,
  MachineryV2Telemetry,
} from "../../fleet/cardStates";
import ProfileLiveRibbon from "../ProfileLiveRibbon";
import { getUiLocale } from "../../../../../shared/utils/relativeTime";

/** Board 20's cell: big mono value over a tiny mono unit caption. */
const KpiCell: React.FC<{ value: string; unit: string }> = ({ value, unit }) => (
  <div
    style={{
      background: "var(--bg-soft)",
      border: "1px solid var(--border-soft)",
      borderRadius: 8,
      padding: "7px 9px",
      minWidth: 0,
    }}
  >
    <div
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 14,
        fontWeight: 700,
        color: "var(--ink)",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </div>
    <div
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 8.5,
        color: "var(--faint)",
        letterSpacing: "0.08em",
        marginTop: 1,
        whiteSpace: "nowrap",
      }}
    >
      {unit}
    </div>
  </div>
);

export interface PrehladLiveCardProps {
  telemetry: MachineryV2Telemetry;
  presentation: MachineryV2CardPresentation;
}

const PrehladLiveCard: React.FC<PrehladLiveCardProps> = ({
  telemetry,
  presentation: p,
}) => {
  const { translate } = useTranslation("myMachinery");
  const dash = "—";

  return (
    <section
      style={{
        background: "var(--card)",
        border: "1px solid var(--border)",
        borderRadius: 10,
        boxShadow: "var(--shadow)",
        padding: "11px 13px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        minWidth: 0,
      }}
    >
      {/* The ribbon owns badge + dot + the ONE footer (alarm strip OR program
          line OR the no-telemetry note). `decommissioned` is false by
          CONSTRUCTION, not by assumption: buildProfileTabs omits the overview
          tab entirely for a retired machine (profileTabContract.ts, ruling D),
          so this component cannot mount for one. */}
      <ProfileLiveRibbon telemetry={telemetry} presentation={p} decommissioned={false} />

      {p.showStatusRow && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
            minWidth: 0,
          }}
        >
          <KpiCell
            value={
              telemetry.spindleRpm === null
                ? dash
                : telemetry.spindleRpm.toLocaleString(getUiLocale())
            }
            unit={translate("myMachinery:v2.profile.overview.kpiRpm")}
          />
          <KpiCell
            value={telemetry.feedPercent === null ? dash : "F " + telemetry.feedPercent}
            unit={translate("myMachinery:v2.profile.overview.kpiFeed")}
          />
        </div>
      )}
    </section>
  );
};

export default PrehladLiveCard;
